/**
 * RegistroExternosPage — Registro de externos (dirección, 2026-09-08, punto 6;
 * tarifas, facturación automática y PDF, 16/10/2026; reglas nuevas, PDF con o
 * sin IVA y tarifas en el Catálogo, 23/10/2026).
 *
 * Facturación del quirófano a los cirujanos EXTERNOS. No tiene entrada de
 * datos propia: se alimenta del Registro de quirófano (partes +
 * intervenciones, GET /api/registro-externos) y enseña SOLO las
 * intervenciones de cirujanos externos —con el cirujano real, no la clínica—.
 *
 * El importe lo calcula el backend con las TARIFAS del Catálogo, bloque
 * «Alquiler quirófano externos» (la única fuente desde el 23/10/2026; el botón
 * «Tarifas (Catálogo)» lleva allí, y allí solo las edita quien tiene la
 * casilla en Usuarios). Si falta una, la fila sale «Pendiente de consulta»,
 * sin importe, y la pantalla lo avisa arriba con el enlace. Todo SIN IVA.
 *
 * Lo que se factura (23/10/2026): «Inicio» (al sedar), «Empieza a contar»
 * (al sedar o, como muy tarde, la cita + cortesía; con otro equipo antes, la
 * entrada real; con el mismo cirujano antes, la separación tras el fin de la
 * anterior), «Fin» (la salida por la puerta) y «Tiempo total». Debajo de cada
 * hora, de qué reloj sale; debajo del tiempo, las horas de la CIRUGÍA.
 *
 * Cada fila se puede ajustar a mano (botón «Cambiar» en el importe): el tipo
 * de facturación, si la primera cirugía de un retoque se hizo en la clínica y
 * PERDONAR EL EXCESO con su motivo (queda quién y cuándo, se deshace
 * quitándolo). Queda en el rastro.
 *
 * «Exportar PDF» pregunta Sin IVA o Con IVA. El Excel se quitó (23/10/2026):
 * solo lo usaba esta pantalla y el PDF lo sustituye.
 *
 * Filtros de Clínica y Cirujano junto a Desde/Hasta, de selección múltiple:
 * una, varias o todas las opciones; la tabla enseña lo que coincida con
 * cualquiera de las marcadas. Los totales respetan los filtros, igual que el
 * PDF.
 *
 * Los partes históricos sin cirujano externo identificable (el nombre de una
 * clínica como cirujano, vacío…) salen en rojo para corregirlos a mano en el parte.
 *
 * Acceso SOLO administradores (dirección y administración), PDF incluido:
 * aquí se ven importes. El backend responde 403 al resto.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router';
import {
  AlertTriangle, ChevronsUpDown, Clock, Euro, FileText, ShieldAlert, UserCog,
} from 'lucide-react';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { api, formatDate, formatEUR, mensajeError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isAdmin } from '../lib/roles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
/**
 * Descarga con la cookie de sesión. `fetch` NO lanza con un 403 ni con un
 * 502: se mira `r.ok` y, si falla, se enseña lo que dice el servidor en vez
 * de bajar su página de error como si fuera el PDF (revisión del
 * 16/10/2026). Devuelve true solo si de verdad se descargó.
 */
async function downloadAuth(url, filename) {
  try {
    const r = await fetch(url, { credentials: 'include' });
    if (!r.ok) {
      let detalle = '';
      try {
        const cuerpo = await r.json();
        detalle = typeof cuerpo?.detail === 'string' ? cuerpo.detail : '';
      } catch { /* sin cuerpo JSON */ }
      toast.error(detalle || `No se pudo descargar (error ${r.status})`);
      return false;
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    return true;
  } catch (e) {
    toast.error(mensajeError(e, 'No se pudo descargar'));
    return false;
  }
}

// Donde viven las tarifas desde el 23/10/2026: el bloque del Catálogo.
const ENLACE_TARIFAS = '/catalogo-cirugias?bloque=alquiler-externos';
const fechaEs = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const fmtMin = (m) => {
  if (m == null || m === '') return '—';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${mm}m` : `${m} min`;
};

// Las del PDF (Fecha, Paciente, Instrumentista, CO2, Citado, Inicio, Empieza a
// contar, Fin, Tiempo total, Importe; las notas, debajo de la fila) y, en la
// pantalla, también la clínica, el cirujano y el procedimiento.
const COLUMNAS = ['Fecha', 'Paciente', 'Clínica', 'Cirujano', 'Instrumentista', 'Procedimiento', 'CO2',
  'Citado', 'Inicio', 'Empieza a contar', 'Fin', 'Tiempo total', 'Importe'];

const TIPOS = [
  { value: 'cirugia', label: 'Cirugía' },
  { value: 'retoque', label: 'Retoque' },
  { value: 'solo_laser', label: 'Solo láser' },
  { value: 'cancelada', label: 'Cancelada (no se cobra)' },
];
const ETIQUETA_TIPO = Object.fromEntries(TIPOS.map((t) => [t.value, t.label]));
// De qué reloj sale cada hora (debajo de la hora).
const RELOJ = {
  sedacion: 'sedación', cirugia: 'inicio cirugía', puerta: 'entrada puerta',
  citado_cortesia: 'cita + cortesía', seguidas: 'fin anterior + separación',
};
const RELOJ_FIN = { puerta: 'salida puerta', cirugia: 'fin cirugía' };

const INFORME_VACIO = {
  filas: [],
  opciones: { clinicas: [], cirujanos: [] },
  totales: {
    intervenciones: 0, con_tiempos: 0, sin_tiempos: 0, minutos: 0, minutos_facturados: 0, importe: 0,
    sin_identificar: 0, pendientes: 0, consultar: 0, canceladas: 0,
  },
  faltanTarifas: [],
  faltanTextos: [],
  avisos: [],
};

/**
 * Selección múltiple con casillas: sin ninguna marcada no se filtra («Todas»);
 * con varias, vale cualquiera de ellas.
 */
function MultiSelect({ opciones, value, onChange, dataTestid, etiquetaVacia = '(sin indicar)' }) {
  const [open, setOpen] = useState(false);
  const texto = value.length === 0
    ? 'Todas'
    : value.length === 1 ? (value[0] || etiquetaVacia) : `${value.length} marcadas`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-52 justify-between font-normal" data-testid={dataTestid}>
          <span className="truncate">{texto}</span>
          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="flex items-center justify-between px-1 pb-1.5 text-xs text-muted-foreground border-b border-[#EEF2F0] mb-1">
          <button type="button" className="hover:underline" onClick={() => onChange([])} data-testid={`${dataTestid}-todas`}>
            Todas (sin filtro)
          </button>
          <button type="button" className="hover:underline" onClick={() => onChange([...opciones])} data-testid={`${dataTestid}-marcar-todas`}>
            Marcar todas
          </button>
        </div>
        {opciones.length === 0 && (
          <div className="px-1 py-2 text-xs text-muted-foreground">Sin opciones en este rango de fechas.</div>
        )}
        {opciones.map((o) => {
          const marcada = value.includes(o);
          return (
            <label key={o || '__vacio__'} className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer rounded hover:bg-[rgba(46,110,94,0.06)]">
              <Checkbox
                checked={marcada}
                onCheckedChange={(v) => onChange(v ? [...value, o] : value.filter((x) => x !== o))}
                data-testid={`${dataTestid}-opcion`}
              />
              <span className={`truncate ${o ? '' : 'text-muted-foreground italic'}`}>{o || etiquetaVacia}</span>
            </label>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

/** Quién es externo: marca en la ficha de cada cirujano (solo admin). */
function CirujanosExternosDialog({ open, onOpenChange, onChanged }) {
  const [cirujanos, setCirujanos] = useState([]);
  const [saving, setSaving] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/api/qx-catalogo', { params: { tipo: 'cirujano' } });
      setCirujanos(Array.isArray(r.data) ? r.data : []);
    } catch {
      setCirujanos([]);
    }
  }, []);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const marcar = async (c, externo) => {
    setSaving(c.id);
    try {
      const r = await api.put(`/api/qx-catalogo/${c.id}`, { externo });
      setCirujanos((prev) => prev.map((x) => (x.id === c.id ? { ...x, externo: !!r.data?.externo } : x)));
      toast.success(`${c.nombre}: ${externo ? 'cirujano externo' : 'colaborador de la clínica'}`);
      onChanged?.();
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo cambiar la marca'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="externos-cirujanos-dialog">
        <DialogHeader>
          <DialogTitle>Cirujanos externos</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Es un dato del cirujano, no de la clínica (hay externos sin clínica). Los marcados facturan el uso del
          quirófano y salen en este registro; los demás son colaboradores de la clínica y no salen.
        </p>
        <div className="divide-y divide-[#EEF2F0]">
          {cirujanos.map((c) => (
            <label key={c.id} className="flex items-center gap-3 py-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!c.externo}
                disabled={saving === c.id}
                onCheckedChange={(v) => marcar(c, !!v)}
                data-testid={`externos-cirujano-marca-${c.id}`}
              />
              <span className="flex-1 truncate">{c.nombre}</span>
              <span className="text-xs text-muted-foreground">{c.externo ? 'Externo' : 'Colaborador'}{c.clinica ? ` · ${c.clinica}` : ''}</span>
            </label>
          ))}
          {cirujanos.length === 0 && <div className="py-3 text-sm text-muted-foreground">No hay cirujanos dados de alta.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * «Exportar PDF» (23/10/2026): Sin IVA (los importes tal cual) o Con IVA (con
 * el tipo de IVA del bloque «Alquiler quirófano externos» del Catálogo, el de
 * la versión de cada intervención).
 */
function ExportarPdfDialog({ open, onOpenChange, onExportar, cirujanos = 0 }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="externos-pdf-dialog">
        <DialogHeader>
          <DialogTitle>Exportar PDF</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          Con los mismos filtros que la tabla, mes a mes con su total.
        </p>
        {/* El PDF se entrega al externo: con varios, se avisa (no se bloquea). */}
        {cirujanos > 1 && (
          <p className="text-sm text-amber-800 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5"
            data-testid="externos-pdf-varios-cirujanos">
            Este PDF lleva {cirujanos} cirujanos: para entregárselo a uno, filtra primero por él.
          </p>
        )}
        <div className="grid gap-2">
          <Button variant="outline" onClick={() => onExportar('sin')} data-testid="externos-export-pdf-sin">
            Sin IVA <span className="ml-2 text-xs text-muted-foreground">los importes tal cual</span>
          </Button>
          <Button variant="outline" onClick={() => onExportar('con')} data-testid="externos-export-pdf-con">
            Con IVA <span className="ml-2 text-xs text-muted-foreground">con el tipo de IVA de las tarifas</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Lo que el administrador fija a mano en una fila (16/10/2026): el tipo de
 * facturación, si la primera cirugía de un retoque se hizo en la clínica y
 * PERDONAR EL EXCESO (23/10/2026; sobre todo si el retraso lo causó otro
 * equipo), con su motivo: el servidor guarda quién y cuándo lo perdonó, y se
 * deshace quitando la marca. Vacío = lo que detecta el sistema. Queda en el
 * rastro.
 */
function AjusteFilaDialog({ fila, onOpenChange, onSaved }) {
  const [tipo, setTipo] = useState('');
  const [primera, setPrimera] = useState('');
  const [noCobrar, setNoCobrar] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!fila) return;
    setTipo(fila.tipo_origen === 'manual' ? fila.tipo : '');
    setPrimera(fila.primera_en_clinica_origen === 'manual' ? (fila.primera_en_clinica ? 'si' : 'no') : '');
    setNoCobrar(!!fila.no_cobrar_exceso);
    setMotivo(fila.motivo_exceso || '');
  }, [fila]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/api/registro-externos/filas/${fila.id}`, {
        tipo: tipo || null,
        primera_en_clinica: primera === '' ? null : primera === 'si',
        no_cobrar_exceso: noCobrar,
        motivo_exceso: noCobrar ? motivo : '',
      });
      toast.success('Facturación de la intervención cambiada');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo cambiar la facturación'));
    } finally {
      setGuardando(false);
    }
  };

  const detectado = fila ? (ETIQUETA_TIPO[fila.tipo_auto] || fila.tipo_auto) : '';
  return (
    <Dialog open={!!fila} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="externos-fila-dialog">
        <DialogHeader>
          <DialogTitle>Facturación de la intervención</DialogTitle>
        </DialogHeader>
        {fila && (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground -mt-2">
              {formatDate(fila.date)} · {fila.patient_name || '—'} · {fila.procedimiento || '—'}
            </p>
            <div>
              <Label htmlFor="fila-tipo" className="text-xs">Tipo de facturación</Label>
              <select id="fila-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}
                className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm" data-testid="externos-fila-tipo">
                <option value="">Automático (detectado: {detectado})</option>
                {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {fila.tipo_motivo && <p className="text-[11px] text-muted-foreground mt-0.5">Detectado porque {fila.tipo_motivo}.</p>}
            </div>
            <div>
              <Label htmlFor="fila-primera" className="text-xs">Retoque: la primera cirugía se hizo…</Label>
              <select id="fila-primera" value={primera} onChange={(e) => setPrimera(e.target.value)}
                className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm" data-testid="externos-fila-primera">
                <option value="">Automático (registro de quirófano y ficha; si no consta, pendiente)</option>
                <option value="si">En la clínica</option>
                <option value="no">En otro sitio</option>
              </select>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={noCobrar} onCheckedChange={(v) => setNoCobrar(!!v)} data-testid="externos-fila-no-cobrar-exceso" />
              <span>
                Perdonar el exceso
                <span className="block text-[11px] text-muted-foreground">Quita las horas extra (por ejemplo, si el retraso lo causó otro equipo). Para deshacerlo, quita la marca.</span>
              </span>
            </label>
            {fila.exceso_perdonado?.por && (
              <p className="text-[11px] text-muted-foreground" data-testid="externos-fila-perdonado">
                Perdonado por {fila.exceso_perdonado.por}{fila.exceso_perdonado.at ? ` el ${fechaEs(fila.exceso_perdonado.at)}` : ''}.
              </p>
            )}
            {noCobrar && (
              <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={300}
                placeholder="Motivo (opcional)" data-testid="externos-fila-motivo" />
            )}
            {fila.ajuste_manual?.por && (
              <p className="text-[11px] text-muted-foreground">Último cambio a mano: {fila.ajuste_manual.por}.</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="externos-fila-guardar">
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Las notas de la fila que no van ya en su celda: las mismas que la columna
 * «Notas» del PDF (23/10/2026), con las «Notas / incidencias» del registro de
 * quirófano las primeras, luego quién operó antes y si se perdonó el exceso.
 */
function notasDeLaFila(r) {
  const yaEnCelda = new Set([r.tiempos_aviso, r.nota_correccion].filter(Boolean));
  const notas = (r.avisos || []).filter((a) => !yaEnCelda.has(a));
  if (r.tipo_aplicado === 'retoque' && r.primera_en_clinica_nota) notas.push(r.primera_en_clinica_nota);
  return notas;
}

/** Una hora con, debajo, de qué reloj sale («sedación», «salida puerta»…). */
function Hora({ hora, reloj }) {
  if (!hora) return '—';
  return (
    <>
      {hora}
      {reloj && <span className="block text-[10px] text-muted-foreground leading-tight">{reloj}</span>}
    </>
  );
}

function claseNota(texto) {
  if (texto.startsWith('Notas / incidencias')) return 'text-[#13231E]';
  if (texto.startsWith('Exceso perdonado')) return 'text-[#2E6E5E] font-medium';
  if (texto.startsWith('Pendiente de consulta')) return 'text-amber-800';
  if (texto.startsWith('Consultar antes de cobrar')) return 'text-red-700 font-medium';
  if (texto.startsWith('Cirujano sin identificar')) return 'text-red-700';
  return 'text-[#2c4a63]';
}

export default function RegistroExternosPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [clinicas, setClinicas] = useState([]);
  const [cirujanos, setCirujanos] = useState([]);
  const [informe, setInforme] = useState(INFORME_VACIO);
  const [loading, setLoading] = useState(true);
  const [externosOpen, setExternosOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [filaAjuste, setFilaAjuste] = useState(null);

  // Mismos parámetros para el informe y el PDF (los filtros múltiples van
  // repetidos: ?clinica=Clinica+A&clinica=Clinica+B).
  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('date_from', dateFrom);
    p.set('date_to', dateTo);
    clinicas.forEach((c) => p.append('clinica', c));
    cirujanos.forEach((c) => p.append('cirujano', c));
    return p;
  }, [dateFrom, dateTo, clinicas, cirujanos]);

  const cargar = useCallback(async () => {
    if (!admin) return;
    setLoading(true);
    try {
      const r = await api.get('/api/registro-externos', { params });
      const d = r.data || {};
      setInforme({
        filas: Array.isArray(d.filas) ? d.filas : [],
        opciones: { clinicas: d.opciones?.clinicas || [], cirujanos: d.opciones?.cirujanos || [] },
        totales: { ...INFORME_VACIO.totales, ...(d.totales || {}) },
        faltanTarifas: Array.isArray(d.faltan_tarifas) ? d.faltan_tarifas : [],
        // Lo que falta en las tarifas de las FECHAS que se ven, con desde
        // cuándo (24/10/2026): «Faltan separación e IVA en la tarifa vigente
        // del 1/10 al 31/10».
        faltanTextos: Array.isArray(d.faltan_tarifas_textos) ? d.faltan_tarifas_textos : [],
        avisos: Array.isArray(d.avisos) ? d.avisos : [],
      });
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo cargar el registro de externos'));
    } finally {
      setLoading(false);
    }
  }, [admin, params]);

  useEffect(() => { cargar(); }, [cargar]);

  const exportarPdf = async (iva) => {
    setPdfOpen(false);
    const p = new URLSearchParams(params);
    p.set('iva', iva);
    const url = `${BACKEND_URL}/api/registro-externos/export-pdf?${p.toString()}`;
    const nombre = `registro_externos_${dateFrom}_${dateTo}_${iva === 'con' ? 'con' : 'sin'}_iva.pdf`;
    if (await downloadAuth(url, nombre)) toast.success(`PDF ${iva === 'con' ? 'con' : 'sin'} IVA descargado`);
  };

  if (!admin) {
    return (
      <div>
        <PageHeader title="Registro de externos" subtitle="Facturación del quirófano a cirujanos externos" />
        <EmptyState icon={ShieldAlert} title="Solo administradores" description="Este registro enseña importes: solo lo ven los administradores." />
      </div>
    );
  }

  const { filas, opciones, totales, faltanTarifas, faltanTextos, avisos } = informe;
  // Cuántos cirujanos lleva el PDF con estos filtros (se entrega a uno).
  const cirujanosDelPdf = new Set(filas.map((f) => f.cirujano || '')).size;
  const hayFiltro = clinicas.length > 0 || cirujanos.length > 0;

  return (
    <div>
      <PageHeader
        title="Registro de externos"
        subtitle="Intervenciones de cirujanos externos del Registro de quirófano, con su importe (sin IVA)"
        actions={
          <>
            {/* Las tarifas se ven y se editan en el Catálogo (23/10/2026). */}
            <Button variant="outline" asChild>
              <Link to={ENLACE_TARIFAS} data-testid="externos-tarifas-btn">
                <Euro className="w-4 h-4 mr-2" /> Tarifas (Catálogo)
              </Link>
            </Button>
            <Button variant="outline" onClick={() => setExternosOpen(true)} data-testid="externos-cirujanos-btn">
              <UserCog className="w-4 h-4 mr-2" /> Cirujanos externos
            </Button>
            <Button onClick={() => setPdfOpen(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="externos-export-pdf">
              <FileText className="w-4 h-4 mr-2" /> Exportar PDF
            </Button>
          </>
        }
      />

      {avisos.length > 0 && (
        <div className="cjo-card p-3 mb-4 border-red-300 bg-red-50 text-sm text-red-800" data-testid="externos-avisos-informe">
          {avisos.map((a) => <div key={a}>▸ {a}</div>)}
        </div>
      )}

      {(faltanTarifas.length > 0 || faltanTextos.length > 0) && (
        <div className="cjo-card p-3 mb-4 border-amber-300 bg-amber-50 flex flex-wrap items-center gap-3" data-testid="externos-faltan-tarifas">
          <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <strong>Faltan tarifas: configúralas.</strong>{' '}
            {faltanTextos.length > 0
              ? faltanTextos.map((t) => <span key={t} className="block" data-testid="externos-faltan-tramo">{t}.</span>)
              : `Faltan ${faltanTarifas.length} tarifa(s) (${faltanTarifas.map((t) => t.etiqueta).join(' · ')}).`}
            {' '}Las intervenciones que las necesitan salen «Pendiente de consulta», sin importe. Se ponen en el
            Catálogo, bloque «Alquiler quirófano externos».
          </div>
          <Button size="sm" variant="outline" asChild>
            <Link to={ENLACE_TARIFAS} data-testid="externos-faltan-tarifas-btn">Ir a las tarifas</Link>
          </Button>
        </div>
      )}

      <div className="cjo-card p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="externos-desde" />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="externos-hasta" />
        </div>
        <div>
          <Label className="text-xs">Clínica</Label>
          <MultiSelect opciones={opciones.clinicas} value={clinicas} onChange={setClinicas} dataTestid="externos-filtro-clinica" etiquetaVacia="(sin clínica)" />
        </div>
        <div>
          <Label className="text-xs">Cirujano</Label>
          <MultiSelect opciones={opciones.cirujanos} value={cirujanos} onChange={setCirujanos} dataTestid="externos-filtro-cirujano" etiquetaVacia="(sin cirujano)" />
        </div>
        {hayFiltro && (
          <Button type="button" variant="ghost" size="sm" onClick={() => { setClinicas([]); setCirujanos([]); }} data-testid="externos-filtros-limpiar">
            Quitar filtros
          </Button>
        )}
        <div className="ml-auto text-sm text-muted-foreground" data-testid="externos-totales">
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <Clock className="w-4 h-4 text-[#2E6E5E]" />
            {totales.intervenciones} intervención(es) · {totales.con_tiempos} con tiempo · total{' '}
            <strong className="text-[#13231E]">{fmtMin(totales.minutos)}</strong> de cirugía · facturadas{' '}
            <strong className="text-[#13231E]" data-testid="externos-minutos-facturados">{fmtMin(totales.minutos_facturados)}</strong> · importe total{' '}
            <strong className="text-[#13231E]" data-testid="externos-importe-total">{formatEUR(totales.importe)}</strong>
            {totales.sin_identificar > 0 && (
              <span className="text-red-700" data-testid="externos-sin-identificar">· {totales.sin_identificar} sin cirujano identificado</span>
            )}
            {totales.sin_tiempos > 0 && (
              <span className="text-amber-700">· {totales.sin_tiempos} sin tiempos</span>
            )}
            {totales.pendientes > 0 && (
              <span className="text-amber-800" data-testid="externos-total-pendientes">· {totales.pendientes} pendiente(s) de consulta, sin importe</span>
            )}
            {totales.consultar > 0 && (
              <span className="text-red-700" data-testid="externos-total-consultar">· {totales.consultar} para consultar antes de cobrar</span>
            )}
            {totales.canceladas > 0 && (
              <span data-testid="externos-total-canceladas">· {totales.canceladas} cancelada(s), no se cobran</span>
            )}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="cjo-card p-8 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : filas.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="Sin intervenciones de externos"
          description={hayFiltro
            ? 'Ninguna intervención coincide con los filtros marcados.'
            : 'No hay intervenciones de cirujanos externos en el Registro de quirófano para ese rango de fechas.'}
        />
      ) : (
        <div className="cjo-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-[#D9E3DD]">
                {/* «Inicio», «Empieza a contar» y «Fin» son lo que se factura
                    (23/10/2026); cada hora lleva debajo de qué reloj sale, y
                    las horas de la CIRUGÍA van debajo del tiempo, con su
                    nombre (dirección, 19/9/2026: que nadie lea una hora de
                    puerta como si fuera la de la cirugía). */}
                {COLUMNAS.map((c) => (
                  <th key={c} className={`py-2.5 px-3 font-medium ${
                    ['Citado', 'Inicio', 'Empieza a contar', 'Fin', 'CO2'].includes(c) ? 'text-center'
                      : ['Tiempo total', 'Importe'].includes(c) ? 'text-right' : ''}`}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => {
                const sinCirujano = !r.cirujano_identificado;
                const d = r.importe_detalle || {};
                const desglose = (d.lineas || [])
                  .map((l) => `${l.concepto}: ${l.importe == null ? 'sin tarifa' : formatEUR(l.importe)}`)
                  .join(' + ') + (r.sin_tiempos ? ' · sin tiempos: faltan las horas extra' : '');
                const notas = notasDeLaFila(r);
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className={`${notas.length ? '' : 'border-b'} border-[#EEF2F0] ${sinCirujano ? 'bg-red-50/70' : ''} ${r.cancelada ? 'opacity-70' : ''}`}
                      data-testid={`externos-row-${r.id}`}
                      title={r.aviso || undefined}
                    >
                      <td className="py-2 px-3 tabular-nums whitespace-nowrap">{formatDate(r.date)}</td>
                      <td className="py-2 px-3 font-medium text-[#13231E]">{r.patient_name || '—'}</td>
                      <td className="py-2 px-3">{r.clinica || '—'}</td>
                      <td className={`py-2 px-3 ${sinCirujano ? 'text-red-700 font-medium' : ''}`} data-testid="externos-cirujano">
                        <span className="inline-flex items-center gap-1">
                          {sinCirujano && <AlertTriangle className="w-3.5 h-3.5" />}
                          {r.cirujano || '(sin cirujano)'}
                        </span>
                      </td>
                      <td className="py-2 px-3" data-testid="externos-instrumentista">
                        {r.instrumentista || '—'}
                        {r.trae_instrumentista_propia && !r.cancelada && (
                          <span className="block text-[10px] text-muted-foreground leading-tight">propia: no se cobra</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{r.procedimiento || '—'}</td>
                      <td className="py-2 px-3 text-center">{r.laser_co2 ? 'Sí' : 'No'}</td>
                      <td className="py-2 px-3 text-center tabular-nums" data-testid="externos-citado">
                        {r.citado || '—'}
                        {r.citado_aviso && (
                          <span className="block text-[10px] text-amber-700 leading-tight mt-0.5 text-left" data-testid="externos-citado-aviso">
                            {r.citado_aviso}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center tabular-nums" data-testid="externos-inicio">
                        <Hora hora={r.inicio_real} reloj={RELOJ[r.inicio_real_origen]} />
                      </td>
                      <td className="py-2 px-3 text-center tabular-nums" data-testid="externos-empieza">
                        <Hora hora={r.cancelada ? '' : r.inicio_facturado} reloj={RELOJ[r.inicio_facturado_origen]} />
                      </td>
                      <td className="py-2 px-3 text-center tabular-nums" data-testid="externos-fin">
                        <Hora hora={r.fin_facturado} reloj={RELOJ_FIN[r.fin_facturado_origen]} />
                      </td>
                      {/* El tiempo que se factura y, debajo, las horas de la
                          CIRUGÍA. Cuando no salen de las firmas del cirujano
                          sino del respaldo escrito a mano en «Parte del día»,
                          la fila lo DICE (dirección, 19/9/2026), y cuando solo
                          faltaba una de las dos horas, dice cuál es cuál
                          (21/9/2026). Y cómo se llega a «Empieza a contar»,
                          con texto que se lee y se imprime. */}
                      <td className={`py-2 px-3 text-right tabular-nums font-medium ${r.tiempos_origen === 'cita' || r.tiempos_origen === 'mixto' ? 'text-amber-700' : 'text-[#13231E]'}`}>
                        {r.cancelada ? '—' : fmtMin(r.duracion_facturada_min)}
                        <span className="block text-[10px] font-normal text-muted-foreground leading-tight mt-0.5" data-testid="externos-cirugia">
                          Cirugía: {r.inicio || '—'}–{r.fin || '—'} ({fmtMin(r.duracion_min)})
                        </span>
                        {r.tiempos_origen === 'cita' && (
                          <span
                            className="block text-[10px] font-normal leading-tight mt-0.5"
                            title={r.tiempos_aviso || undefined}
                            data-testid="externos-tiempos-respaldo"
                          >
                            Sin firmas del cirujano: horas escritas a mano en «Parte del día».
                            Compruébalas antes de facturar.
                          </span>
                        )}
                        {r.tiempos_origen === 'mixto' && (
                          <span
                            className="block text-[10px] font-normal leading-tight mt-0.5 text-left"
                            data-testid="externos-tiempos-a-medias"
                          >
                            {r.tiempos_aviso}
                          </span>
                        )}
                        {/* LO QUE DIJO LA FIRMA, A LA VISTA (dirección, 21/9/2026). */}
                        {r.nota_correccion && (
                          <span
                            className="block text-[10px] font-normal leading-tight mt-0.5 text-left text-amber-700"
                            data-testid="externos-nota-correccion"
                          >
                            {r.nota_correccion}
                          </span>
                        )}
                        {!r.cancelada && r.inicio_facturado_texto && (
                          <span
                            className="block text-[10px] font-normal leading-tight mt-0.5 text-left text-[#2c4a63]"
                            data-testid="externos-facturado"
                          >
                            {r.duracion_facturada_min == null ? 'No se sabe cuánto se factura. ' : ''}
                            {r.inicio_facturado_texto}
                          </span>
                        )}
                      </td>
                      <td className={`py-2 px-3 text-right tabular-nums font-medium ${r.sin_tiempos || r.pendiente ? 'text-amber-700' : 'text-[#13231E]'}`} title={desglose || undefined} data-testid="externos-importe">
                        {r.cancelada ? '0 €' : r.pendiente ? 'Pendiente de consulta' : formatEUR(r.importe)}
                        {r.sin_tiempos && !r.pendiente && <span className="block text-[10px] font-normal">sin tiempos</span>}
                        <span className="block text-[10px] font-normal text-muted-foreground" data-testid="externos-tipo">
                          {r.tipo_etiqueta || ETIQUETA_TIPO[r.tipo] || ''}{r.tipo_origen === 'manual' ? ' (a mano)' : ''}
                          {r.tipo_aplicado && r.tipo_aplicado !== r.tipo ? ` → ${ETIQUETA_TIPO[r.tipo_aplicado] || r.tipo_aplicado}` : ''}
                        </span>
                        <button
                          type="button"
                          className="text-[11px] font-normal text-[#2E6E5E] hover:underline"
                          onClick={() => setFilaAjuste(r)}
                          data-testid={`externos-fila-editar-${r.id}`}
                        >
                          Cambiar
                        </button>
                      </td>
                    </tr>
                    {notas.length > 0 && (
                      <tr className={`border-b border-[#EEF2F0] ${sinCirujano ? 'bg-red-50/70' : ''}`} data-testid={`externos-notas-${r.id}`}>
                        <td colSpan={COLUMNAS.length} className="px-3 pb-2 pt-0 text-[11px] leading-snug">
                          {notas.map((n) => (
                            <span key={n} className={`mr-3 inline-block ${claseNota(n)}`}>▸ {n}</span>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-xs text-muted-foreground space-y-1" data-testid="externos-reglas">
        <p>
          Importe por intervención según las tarifas del Catálogo, bloque «Alquiler quirófano externos»:
          cirugía (sin o con CO2, con sus minutos incluidos, horas extra e instrumentista), retoque (según si la
          primera cirugía se hizo en la clínica o en otro sitio y si lleva sedación) o solo láser (periocular o full
          face, y anestesia), con la versión de las tarifas vigente en la fecha de cada intervención. Empieza a
          contar al sedar o, como muy tarde, a la hora de la cita más la cortesía; si antes operaba otro equipo en
          el mismo sitio y seguía en la sala, desde la entrada real, sin cortesía (el exceso se puede perdonar en
          «Cambiar»); si era el mismo cirujano, la separación de la tarifa después de que acabara la anterior.
          Acaba cuando el paciente sale por la puerta. Si falta una tarifa o un dato para elegirla, la fila sale
          «Pendiente de consulta»; si algo no cuadra, «Consultar antes de cobrar».
        </p>
        <p data-testid="externos-aviso-iva">
          Importes SIN IVA. Para facturar sin IVA hace falta un informe justificativo, que la aplicación
          todavía no genera.
        </p>
        {totales.sin_identificar > 0 && (
          <p className="text-red-700">
            En rojo: partes cuyo cirujano no se identifica como externo (por ejemplo, la clínica escrita como cirujano). Corrígelos en el
            parte del Registro de quirófano eligiendo el cirujano de la lista.
          </p>
        )}
      </div>

      <CirujanosExternosDialog open={externosOpen} onOpenChange={setExternosOpen} onChanged={cargar} />
      <ExportarPdfDialog open={pdfOpen} onOpenChange={setPdfOpen} onExportar={exportarPdf}
        cirujanos={cirujanosDelPdf} />
      <AjusteFilaDialog fila={filaAjuste} onOpenChange={(v) => { if (!v) setFilaAjuste(null); }} onSaved={cargar} />
    </div>
  );
}
